import {Modal, Box, styled, Button, Stack} from '@mui/material'
import LocationPicker from 'web-common/components/location/LocationPicker'
import TimelineEventTemplate from 'web-common/views/timeline/TimelineEventTemplate'
import {withTranslation, WithTranslation} from 'react-i18next'
import {AssistantEventProducer} from '../AssistantEventProducer'
import {ChatAdapter} from '@fnd/timeline'
import {LocationAdd} from 'iconsax-reactjs'

export const LocationBoxModal = styled(Box)(({theme}) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  height: '80%',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[24],
  padding: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column'
}))

interface MapLocationPickerProps {
  variant: 'location' | 'range'
  buttonLabel: string
  position: 'flex-start' | 'center' | 'flex-end'
  producer: AssistantEventProducer
}

interface InternalProps extends MapLocationPickerProps, WithTranslation {}

interface Location {
  lat: number
  lon: number
}

interface LocationValue extends Location {
  address: string
  regions?: string[]
  range?: number
}

interface MapLocationPickerState {
  open: boolean
  value?: LocationValue
}

class MapLocationPicker extends TimelineEventTemplate<
  InternalProps,
  MapLocationPickerState
> {
  constructor(props: any) {
    super(props)
    this.state = {
      open: false,
      value: undefined
    }
  }

  openLocationDialog = () => {
    this.setState({open: true})
  }

  closeLocationDialog = () => {
    this.setState({open: false})
  }

  handleLocationChange = (
    location: Location,
    address: string,
    regions?: string[],
    range?: number
  ) => {
    this.setState({
      value: {
        lat: location.lat,
        lon: location.lon,
        address,
        regions,
        range
      }
    })
  }

  handleConfirm = async () => {
    const {value} = this.state
    const {variant, producer} = this.props

    if (value) {
      const regionsList =
        variant === 'location'
          ? value.regions && value.regions.length > 0
            ? [value.regions[0]]
            : []
          : []

      const officesList = variant === 'range' ? value.regions || [] : []
      const message = value.range
        ? value.address + `\nRange: ${value.range / 1000}km`
        : value.address

      await producer.createEvent({
        message,
        adapterId: ChatAdapter.id,
        data: {
          metadata: {
            action: 'SHARED_LOCATION',
            location: {
              regions: regionsList,
              offices: officesList,
              address: value.address,
              lat: value.lat,
              lon: value.lon,
              range: value.range
            }
          }
        }
      })

      producer.actionManager.removeLocationPicker()
      this.closeLocationDialog()
    }
  }

  render() {
    return (
      <Stack spacing={2} alignItems={this.props.position} marginTop={1}>
        <Button
          startIcon={<LocationAdd />}
          variant="contained"
          color="primary"
          size="medium"
          sx={{borderRadius: 3}}
          disabled={this.props.producer.hasDemandId()}
          onClick={this.openLocationDialog}
        >
          {this.props.buttonLabel}
        </Button>
        <Modal open={this.state.open} onClose={this.closeLocationDialog}>
          <LocationBoxModal>
            <Box flex={1} overflow="hidden">
              <LocationPicker
                country="il"
                variant={this.props.variant}
                value={this.state.value}
                onChange={this.handleLocationChange}
              />
            </Box>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
              paddingTop={2}
            >
              <Button
                variant="contained"
                color="inherit"
                size="large"
                onClick={this.closeLocationDialog}
              >
                {this.props.t('fnd-assistant-common-cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={this.handleConfirm}
                disabled={!this.state.value}
              >
                {this.props.t('fnd-assistant-common-confirm')}
              </Button>
            </Stack>
          </LocationBoxModal>
        </Modal>
      </Stack>
    )
  }
}

export default withTranslation()(MapLocationPicker)
